import { APIError, APIClientError } from '../../lib/errors';
import Events from '../../lib/events';

// response helper functions ----------------------------------------------- //

const convert_to_js = async data => {
  if (
    typeof data === 'object' &&
    'json' in data &&
    typeof data.json === 'function'
  ) {
    return await data.json();
  } else {
    return data;
  }
};

// request builder functions ----------------------------------------------- //

const get_browser_info = () => {
  try {
    const browser = window.navigator.browserSpecs;
    return `${browser.name},${browser.version}`;
    // eslint-disable-next-line no-empty
  } catch (e) {}

  return 'unknown';
};

const detect_mobile = () => {
  const to_match = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];

  return to_match.some(item => {
    return navigator.userAgent.match(item);
  });
};

const build_request_headers = (method = 'GET', headers = {}) => {
  return {
    method: method,
    headers: {
      ...headers,
      'content-type': 'application/json',
      'WP-Platform': 'web',
      'WP-Is-Phone': detect_mobile(),
      'WP-Browser': get_browser_info(),
    },
  };
};

const build_url_params = data => {
  let params_array = Object.entries(data);

  let divider = item => {
    return item === params_array[0] ? '?' : '&';
  };

  return params_array.reduce((acc, item) => {
    return `${acc}${divider(item)}${item[0]}=${item[1]}`;
  }, '');
};

export const build_request_params = context => {
  let url = context.endpoint;

  let options = build_request_headers(context.method, context.headers);

  if (context.data && ['GET', 'HEAD'].includes(context.method.toUpperCase())) {
    let params = build_url_params(context.data);

    url = `${url}${params}`;
  } else if (context.data) {
    options.body = JSON.stringify(context.data);
  }

  context.url = url;
  context.options = options;

  return { url, options };
};

// fetch-enhancing functions ----------------------------------------------- //

const fetch_with_timeout = async (url, options, timeout, signal) => {
  setTimeout(signal.abort, timeout);

  return await fetch(url, { ...options, signal });
};

const fetch_with_retries_and_timeout = async context => {
  const throttle_time = context.retry_throttle[context.retries];

  return new Promise(resolve => {
    setTimeout(async () => {
      if (context.timeout > 0) {
        return resolve(
          await fetch_with_timeout(
            context.url,
            context.options,
            context.timeout,
            context.signal
          )
        );
      } else {
        return resolve(await fetch(context.url, context.options));
      }
    }, throttle_time);
  });
};

const parse_response = async (context, response) => {
  if (context.data_type === 'json') {
    return await convert_to_js(response);
  } else {
    return response;
  }
};

export const request = async context => {
  try {
    const response = await fetch_with_retries_and_timeout(context);

    if (response.status >= 200 && response.status < 300) {
      return parse_response(context, response);
    } else if (response.status === 401) {
      return Events.dispatch('wp:api:unauthenticated');
    } else if (response.status === 402) {
      return Events.dispatch('wp:api:versionupdateneeded');
    } else if (response.status === 404) {
      let error_data = await convert_to_js(response);
      throw new APIClientError('NotFound', {
        response,
        error_data,
      });
    } else if (response.status >= 400) {
      let error_data = await convert_to_js(response);
      throw new APIClientError(error_data[0].code, {
        response,
        error_data,
      });
    } else if (response.status >= 500) {
      let error_data = await convert_to_js(response);
      throw new APIError('Server error', { error_data });
    }
  } catch (error) {
    return Promise.reject(error);
  }
};
